import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { 
  isExtendedTubeLevel,
  hasHiddenPieces,
  hasGravityMode,
  hasTimeLimit
} from './gameLogic';  // Adjust path as needed

const ModeInfo = ({ isHardMode, level, theme }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Get current active challenges based on level using challenge system exports
  const getCurrentChallenges = () => {
    if (!isHardMode) return [];
    
    const challenges = [];
    
    if (isExtendedTubeLevel(level, isHardMode)) {
      challenges.push({
        name: "Extended Tubes",
        description: "Tubes are 5 units tall instead of 4"
      });
    }
    
    if (hasHiddenPieces(level, isHardMode)) {
      challenges.push({
        name: "Hidden Pieces",
        description: "Only the top crystal in each tube is visible"
      });
    }

    if (hasGravityMode(level, isHardMode)) {
      challenges.push({
        name: "Gravity Mode",
        description: "Crystals always fall to the bottom of the tube when moved"
      });
    }

    if (hasTimeLimit(level, isHardMode)) {
        challenges.push({
          name: "Time Limit",
          description: "Complete the level before time runs out"
        });
      }
    
    return challenges;
  };

  const activeChallenges = getCurrentChallenges();

  return (
    <div className="flex items-center">
      {isHardMode && (
        <div className={`mr-6 ${theme.text}`}>
          <div className="font-medium mb-1">Active Challenges:</div>
          <div className="text-sm">
            {activeChallenges.length > 0 ? (
              activeChallenges.map((challenge, index) => (
                <span key={challenge.name} className="mr-2">
                  {challenge.name}{index < activeChallenges.length - 1 ? ' • ' : ''}
                </span>
              ))
            ) : (
              <span>None</span>
            )}
          </div>
        </div>
      )}
      
      <div className="relative">
        <div
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-help"
        >
          <HelpCircle className={`w-5 h-5 ${theme.text} hover:opacity-70`} />
        </div>
        
        {showTooltip && (
          <div className={`
            absolute z-50 right-0 mt-2 w-[300px]
            ${theme.container} rounded-lg shadow-lg p-4 border ${theme.border}
            transform translate-x-2
          `}>
            <div className="space-y-3">
              <h3 className={`font-bold ${theme.text}`}>
                {isHardMode ? "Challenge Mode Rules" : "Normal Mode Rules"}
              </h3>
              
              {isHardMode ? (
                <>
                  <div>
                    <h4 className={`font-semibold ${theme.text}`}>Gravity Mode</h4>
                    <p className={`text-sm ${theme.subtext}`}>
                      Crystals always fall to the bottom of tubes when moved.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className={`font-semibold ${theme.text}`}>Extended Tubes</h4>
                    <p className={`text-sm ${theme.subtext}`}>
                      These levels feature taller tubes with more sections.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className={`font-semibold ${theme.text}`}>Hidden Pieces</h4>
                    <p className={`text-sm ${theme.subtext}`}>
                      These levels include hidden pieces - only the top crystal in each tube is visible.
                    </p>
                  </div>

                  <div>
                    <h4 className={`font-semibold ${theme.text}`}>Time Limit</h4>
                    <p className={`text-sm ${theme.subtext}`}>
                      There is a time limit. Once you start, you'll have to finish quick or restart.
                    </p>
                  </div>
                </>
              ) : (
                <p className={`text-sm ${theme.subtext}`}>
                  Standard game rules apply. Sort crystals by color into tubes, with each tube holding up to 4 crystals. You can only move crystals between tubes when they match the color at the destination.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeInfo;